import { useCallback, useEffect, useRef, useState } from 'react'
import { enqueueSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { postDeviceComposition } from '@/api/calibration'
import type { Device } from '@/pages/Device/Device'
import { Composition } from '@/pages/calibration/composition'
interface Args {
  device: Device
  composition: Composition | null
  finishSelfTest: (message: Record<string, unknown>) => void
  lastJsonMessage: Record<string, unknown> | null
  setActiveStep: (step: number) => void
}

export default function useFinishVerification({
  device,
  composition,
  finishSelfTest,
  lastJsonMessage,
  setActiveStep,
}: Args) {
  const { t } = useTranslation(['app'])
  const [isSaving, setIsSaving] = useState(false)
  const pendingFinishData = useRef<Record<string, unknown> | null>(null)

  const lastJsonMessageAtConfirmRef = useRef<Record<string, unknown> | null>(
    null,
  )

  const confirm = useCallback(
    (finishData: Record<string, unknown>) => {
      if (!composition) {
        enqueueSnackbar(t('app:device.calibration.verifyStep.noComposition'), {
          variant: 'error',
        })
        return
      }
      pendingFinishData.current = finishData
      lastJsonMessageAtConfirmRef.current = lastJsonMessage
      setIsSaving(true)
      finishSelfTest({ type: 'confirmed', data: finishData })
    },
    [composition, finishSelfTest, lastJsonMessage, t],
  )

  useEffect(() => {
    if (lastJsonMessage?.type !== 'finished') return
    if (!pendingFinishData.current || !composition) return
    if (lastJsonMessage === lastJsonMessageAtConfirmRef.current) return

    void (async () => {
      try {
        await postDeviceComposition(device.uid, composition.configuration.id)
        enqueueSnackbar(t('app:device.calibration.verifyStep.complete'), {
          variant: 'success',
          autoHideDuration: 10000,
        })
        setActiveStep(0)
      } catch {
        // Caught by interceptor
      } finally {
        setIsSaving(false)
        pendingFinishData.current = null
        lastJsonMessageAtConfirmRef.current = null
      }
    })()
  }, [lastJsonMessage, composition, device.uid, setActiveStep, t])

  return { isSaving, confirm }
}
